// Simple frontend logger for React application

export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
}

class Logger {
    private readonly level: LogLevel;
    private readonly isDevelopment: boolean;

    constructor() {
        // Vite uses import.meta.env.DEV for development mode detection
        this.isDevelopment = import.meta.env.DEV || process.env.NODE_ENV === "development";
        // Set log level based on environment
        this.level = LogLevel.DEBUG; // this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
    }

    private shouldLog(level: LogLevel): boolean {
        return level >= this.level;
    }

    private formatMessage(level: string, message: string, ...args: any[]): void {
        const timestamp = new Date().toISOString();
        const formattedMessage = `[${timestamp}] [${level}] ${message}`;

        if (this.isDevelopment && level === "DEBUG") {
            console.log(formattedMessage, ...args);
            return;
        }

        switch (level) {
            case "DEBUG":
                console.debug(formattedMessage, ...args);
                break;
            case "INFO":
                console.info(formattedMessage, ...args);
                break;
            case "WARN":
                console.warn(formattedMessage, ...args);
                break;
            case "ERROR":
                console.error(formattedMessage, ...args);
                break;
        }
    }

    debug(message: string, ...args: any[]): void {
        if (this.shouldLog(LogLevel.DEBUG)) {
            this.formatMessage("DEBUG", message, ...args);
        }
    }

    info(message: string, ...args: any[]): void {
        if (this.shouldLog(LogLevel.INFO)) {
            this.formatMessage("INFO", message, ...args);
        }
    }

    warn(message: string, ...args: any[]): void {
        if (this.shouldLog(LogLevel.WARN)) {
            this.formatMessage("WARN", message, ...args);
        }
    }

    error(message: string, ...args: any[]): void {
        if (this.shouldLog(LogLevel.ERROR)) {
            this.formatMessage("ERROR", message, ...args);
        }
    }

    // Convenience method for logging objects
    logObject(level: "debug" | "info" | "warn" | "error", message: string, obj: any): void {
        this[level](message, JSON.stringify(obj, null, 2));
    }

    warnObject(obj: any, message: string): void {
        this["warn"](message, JSON.stringify(obj, null, 2));
    }
}

// Export singleton instance
const logger = new Logger();
export default logger;
